import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { cn } from "@/lib/utils";

function Select({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        // Base styles matching input field from Figma
        "aero:flex aero:w-full aero:items-center aero:justify-between aero:gap-2",
        "aero:h-10 aero:px-4 aero:py-3",
        "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
        "aero:shadow-none",
        "aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
        "aero:text-foreground aero:whitespace-nowrap",
        // Placeholder state
        "aero:data-[placeholder]:text-neutral-500",
        // Focus states
        "aero:focus-visible:outline-none aero:focus-visible:border-ring aero:focus-visible:ring-ring/50 aero:focus-visible:ring-[3px]",
        // Error states
        "aero:aria-invalid:border-destructive aero:aria-invalid:ring-destructive/20",
        // Disabled state
        "aero:disabled:cursor-not-allowed aero:disabled:opacity-50",
        // Icon styling
        "aero:[&_svg:not([class*='text-'])]:text-neutral-500",
        "aero:[&_svg]:pointer-events-none aero:[&_svg]:shrink-0 aero:[&_svg:not([class*='size-'])]:size-4",
        // Select value styling
        "aero:*:data-[slot=select-value]:line-clamp-1 aero:*:data-[slot=select-value]:flex aero:*:data-[slot=select-value]:items-center aero:*:data-[slot=select-value]:gap-2",
        // Size variants
        "aero:data-[size=sm]:h-8",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="aero:size-4 aero:opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "aero:bg-popover aero:text-popover-foreground aero:data-[state=open]:animate-in aero:data-[state=closed]:animate-out aero:data-[state=closed]:fade-out-0 aero:data-[state=open]:fade-in-0 aero:data-[state=closed]:zoom-out-95 aero:data-[state=open]:zoom-in-95 aero:data-[side=bottom]:slide-in-from-top-2 aero:data-[side=left]:slide-in-from-right-2 aero:data-[side=right]:slide-in-from-left-2 aero:data-[side=top]:slide-in-from-bottom-2 aero:relative aero:z-50 aero:max-h-(--radix-select-content-available-height) aero:min-w-[8rem] aero:origin-(--radix-select-content-transform-origin) aero:overflow-x-hidden aero:overflow-y-auto aero:!rounded-md aero:border aero:shadow-md",
          position === "popper" &&
            "aero:data-[side=bottom]:translate-y-1 aero:data-[side=left]:-translate-x-1 aero:data-[side=right]:translate-x-1 aero:data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "aero:p-1",
            position === "popper" &&
              "aero:h-[var(--radix-select-trigger-height)] aero:w-full aero:min-w-[var(--radix-select-trigger-width)] aero:scroll-my-1"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        "aero:text-muted-foreground aero:px-2 aero:py-1.5 aero:text-xs",
        className
      )}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "aero:focus:bg-accent aero:focus:text-accent-foreground aero:[&_svg:not([class*='text-'])]:text-muted-foreground aero:relative aero:flex aero:w-full aero:cursor-default aero:items-center aero:gap-2 aero:!rounded-sm aero:py-1.5 aero:pr-8 aero:pl-2 aero:text-sm aero:outline-hidden aero:select-none aero:data-[disabled]:pointer-events-none aero:data-[disabled]:opacity-50 aero:[&_svg]:pointer-events-none aero:[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 aero:*:[span]:last:flex aero:*:[span]:last:items-center aero:*:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <span className="aero:absolute aero:right-2 aero:flex aero:size-3.5 aero:items-center aero:justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="aero:size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn(
        "aero:bg-border aero:pointer-events-none aero:-mx-1 aero:my-1 aero:h-px",
        className
      )}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "aero:flex aero:cursor-default aero:items-center aero:justify-center aero:py-1",
        className
      )}
      {...props}
    >
      <ChevronUpIcon className="aero:size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "aero:flex aero:cursor-default aero:items-center aero:justify-center aero:py-1",
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="aero:size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
